import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import useIntlPolicies from "~/src/shared/hooks/useIntlPolicies";
import { IPlanTier } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
// import AccountIcon from "../../../icons/VendorProfile/AccountIcon";
// import TeamIcon from "../../../icons/VendorProfile/TeamIcon";
// import EditProfileIcon from "../../../icons/VendorProfile/EditProfileIcon";
import MakersProIcon from "~/components/icons/MakersProIcon";
import ActionsIcon from "~/components/icons/VendorProfile/ActionsIcon";
// import PreviewIcon from "../../../icons/PreviewIcon";
import ActivityIcon from "~/components/icons/VendorProfile/ActivityIcon";
import GovernmentsIcon from "~/components/icons/VendorProfile/GovernmentsIcon";
import GroupsIcon from "~/components/icons/VendorProfile/GroupsIcon";
import ProductsIcon from "~/components/icons/VendorProfile/ProductsIcon";
import ProjectsIcon from "~/components/icons/VendorProfile/ProjectsIcon";
import SettingsIcon from "~/components/icons/VendorProfile/SettingsIcon";
import SharingHubIcon from "~/components/icons/VendorProfile/SharingHubIcon";
import StoriesIcon from "~/components/icons/VendorProfile/StoriesIcon";
import Terms from "~/components/Terms";
import getIntercomTarget from "~/components/vendor/profile/getIntercomTarget";
import VendorOwnerNavbar from "~/components/vendor/profile/owner/VendorOwnerNavbar";
import VendorOwnerNavbarGroup from "~/components/vendor/profile/owner/VendorOwnerNavbarGroup";
import VendorOwnerNavbarTab from "~/components/vendor/profile/owner/VendorOwnerNavbarTab";

interface IVendorOwnerNavigationProps {
  vendor: NonNullable<IVendorQuery["company"]>;
  tab: string;
}

export default function VendorOwnerNavigation({ vendor, tab }: IVendorOwnerNavigationProps) {
  const intl = useIntl();
  const user = useSelfie();
  const policiesUrls = useIntlPolicies();

  if (!user || user.__typename !== "VendorUser") {
    return null;
  }

  const userCanSeeAccountTab =
    user.role === "COMPANY_OWNER" || (vendor.usersCanAccessAccountPage && vendor.viewerIsMember);

  return (
    <VendorOwnerNavbar>
      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/connect`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={ActivityIcon}
        title={intl.formatMessage({
          defaultMessage: 'Activity', id: 'ZmlNQ3',
        })}
        alwaysShowCounter={true}
        count={vendor.feedCount}
        disabled={vendor.plan.tier === IPlanTier.Basic}
        active={tab === "connect"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Activity Navbar Tab",
          asOwner: "Activity Navbar Tab",
          asDisruptor: "Activity Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/products`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={ProductsIcon}
        title={intl.formatMessage({
          id: '7NFfmz',
          defaultMessage: "Products",
        })}
        count={vendor.productsCount}
        active={tab === "products"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Products Navbar Tab",
          asOwner: "Products Navbar Tab",
          asDisruptor: "Products Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/projects`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={ProjectsIcon}
        title={intl.formatMessage({
          defaultMessage: 'Projects', id: 'UxTJRa',
        })}
        disabled={vendor.plan.limits.projectsPerMonth === 0}
        count={vendor.projectsCount}
        active={tab === "projects"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Projects Navbar Tab",
          asOwner: "Projects Navbar Tab",
          asDisruptor: "Projects Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarGroup
        href="/vendors/[vendorSlug]/[[...content]]"
        as={`/vendors/${vendor.slug}/invite-governments`}
        icon={GovernmentsIcon}
        title={intl.formatMessage({
          defaultMessage: 'Governments', id: 'qnQX9U',
        })}
        disabled={!vendor.plan.limits.inviteGovernments}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Governments Navbar Tab",
          asOwner: "Governments Navbar Tab",
          asDisruptor: "Governments Navbar Tab for Disruptors",
        })}
        count={vendor.governmentConnectionsCount}
        active={tab === "invite-governments" || tab === "governments"}
      >
        <VendorOwnerNavbarTab
          as={`/vendors/${vendor.slug}/invite-governments`}
          href="/vendors/[vendorSlug]/[[...content]]"
          icon={null}
          title={intl.formatMessage({
            defaultMessage: 'Invite Governments', id: 'ZiH9ot',
          })}
          disabled={!vendor.plan.limits.inviteGovernments}
          active={tab === "invite-governments"}
        />
        <VendorOwnerNavbarTab
          as={`/vendors/${vendor.slug}/governments`}
          href="/vendors/[vendorSlug]/[[...content]]"
          icon={null}
          title={intl.formatMessage({
            defaultMessage: 'Verified Governments', id: '1cTtuP',
          })}
          disabled={!vendor.plan.limits.inviteGovernments}
          active={tab === "governments"}
        />
      </VendorOwnerNavbarGroup>

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/stories`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={StoriesIcon}
        title={intl.formatMessage({
          defaultMessage: 'Stories', id: 'Ldxym4',
        })}
        disabled={vendor.plan.tier === IPlanTier.Basic}
        count={vendor.storiesCount}
        active={tab === "stories"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Stories Navbar Tab",
          asOwner: "Stories Navbar Tab",
          asDisruptor: "Stories Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/groups`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={GroupsIcon}
        title={intl.formatMessage({
          id: 'hzmswI',
          defaultMessage: "Groups",
        })}
        count={vendor.groups.length}
        disabled={vendor.plan.limits.groups + vendor.plan.limits.groupInvitations === 0}
        active={tab === "groups"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Groups Navbar Tab",
          asOwner: "Groups Navbar Tab",
          asDisruptor: "Groups Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/actions`}
        href="/vendors/[vendorSlug]/[[...content]]"
        alwaysShowCounter={true}
        icon={ActionsIcon}
        title={intl.formatMessage({
          defaultMessage: 'Actions', id: 'wL7VAE',
        })}
        disabled={vendor.plan.tier === IPlanTier.Basic}
        count={vendor.actionsCount}
        active={tab === "actions"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Actions Navbar Tab",
          asOwner: "Actions Navbar Tab",
          asDisruptor: "Actions Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarTab
        as={`/vendors/${vendor.slug}/sharing`}
        href="/vendors/[vendorSlug]/[[...content]]"
        icon={SharingHubIcon}
        planIcon={MakersProIcon}
        disabled={!vendor.plan.limits.sharingPage}
        title={intl.formatMessage({
          defaultMessage: 'Sharing Hub', id: '88oiPf',
        })}
        active={tab === "sharing"}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Sharing Hub Navbar Tab",
          asOwner: "Sharing Hub Navbar Tab",
          asDisruptor: "Sharing Hub Navbar Tab for Disruptors",
        })}
      />

      <VendorOwnerNavbarGroup
        href="/vendors/[vendorSlug]/[[...content]]"
        as={`/vendors/${vendor.slug}/${userCanSeeAccountTab ? "account" : "edit-vendor"}`}
        css={{
          marginBottom: ["account", "change-plans", "edit-vendor", "preview-profile", "team"].includes(tab) ? -16 : 0,
        }}
        icon={SettingsIcon}
        title={intl.formatMessage({
          defaultMessage: 'Settings', id: 'D3idYv',
        })}
        active={["account", "change-plans", "edit-vendor", "preview-profile", "team"].includes(tab)}
        dataIntercomTarget={getIntercomTarget(user, {
          asMember: "Settings Navbar Tab",
          asOwner: "Settings Navbar Tab",
          asDisruptor: "Settings Navbar Tab for Disruptors",
        })}
      >
        {userCanSeeAccountTab && (
          <VendorOwnerNavbarTab
            as={`/vendors/${vendor.slug}/account`}
            href="/vendors/[vendorSlug]/[[...content]]"
            // icon={AccountIcon}
            icon={null}
            title={intl.formatMessage({
              defaultMessage: 'Account', id: 'TwyMau',
            })}
            active={tab === "account" || tab === "change-plans"}
            css={{
              padding: "8px 16px",
            }}
            dataIntercomTarget={getIntercomTarget(user, {
              asMember: "Account Navbar Tab",
              asOwner: "Account Navbar Tab",
              asDisruptor: "Account Navbar Tab for Disruptors",
            })}
          />
        )}

        <VendorOwnerNavbarTab
          as={`/vendors/${vendor.slug}/edit-vendor`}
          href="/vendors/[vendorSlug]/[[...content]]"
          // icon={EditProfileIcon}
          icon={null}
          title={intl.formatMessage({
            defaultMessage: 'Edit Profile', id: 'usAvMr',
          })}
          active={tab === "edit-vendor"}
          css={{
            padding: "8px 16px",
          }}
          dataIntercomTarget={getIntercomTarget(user, {
            asMember: "Edit Vendor Navbar Tab",
            asOwner: "Edit Vendor Navbar Tab",
            asDisruptor: "Edit Vendor Navbar Tab for Disruptors",
          })}
        />

        <VendorOwnerNavbarTab
          as={
            vendor.plan.limits.enrichedProfile
              ? `/vendors/${vendor.slug}/limits?preview=1&makers=1`
              : `/vendors/${vendor.slug}/limits?preview=1`
          }
          href="/vendors/[vendorSlug]/[[...content]]"
          // icon={PreviewIcon}
          icon={null}
          title={intl.formatMessage({
            defaultMessage: 'Preview Profile', id: 'Uve4oA',
          })}
          active={tab === "preview-profile"}
          css={{
            padding: "8px 16px",
          }}
          dataIntercomTarget={getIntercomTarget(user, {
            asMember: "Preview Profile Navbar Tab",
            asOwner: "Preview Profile Navbar Tab",
            asDisruptor: "Preview Profile Navbar Tab for Disruptors",
          })}
        />

        <VendorOwnerNavbarTab
          as={`/vendors/${vendor.slug}/team`}
          href="/vendors/[vendorSlug]/[[...content]]"
          // icon={TeamIcon}
          icon={null}
          title={intl.formatMessage({
            defaultMessage: 'Team', id: 'wsUmh9',
          })}
          active={tab === "team"}
          css={{
            padding: "8px 16px",
          }}
          dataIntercomTarget={getIntercomTarget(user, {
            asMember: "Team Navbar Tab",
            asOwner: "Team Navbar Tab",
            asDisruptor: "Team Navbar Tab for Disruptors",
          })}
        />

        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Terms
            title={intl.formatMessage({
              defaultMessage: 'Terms and Conditions', id: '6qF/vN',
            })}
            policyUrl={policiesUrls.termsAndConditions}
          >
            {(toggleOpen) => (
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  toggleOpen();
                }}
                css={{
                  padding: "8px 0 8px 16px",
                  display: "flex",
                  opacity: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#5A6677",
                  fontWeight: "normal",
                  fontSize: 16,
                }}
              >
                <FormattedMessage defaultMessage="Terms of Service" id="32rBNK" />
              </a>
            )}
          </Terms>
        </div>
      </VendorOwnerNavbarGroup>
    </VendorOwnerNavbar>
  );
}
